/**
 * ClaimLorStep
 *
 * Handles the last phase of claim filing: confirming final details required
 * for a Letter of Representation (LoR), generating the LoR from templates,
 * previewing documents, and sending via fax with lightweight status UI.
 * Now includes an embedded FinchGPT assistant for contextual QA.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ExternalLink,
  FileText,
  Maximize2,
  MessageSquare,
  Pencil,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { AgentChatDrawer } from '@/components/agent-chat/AgentChatDrawer';
import { AgentChatThread } from '@/components/agent-chat/AgentChatThread';
import { extractFieldReviewMapFromMessages } from '@/components/agent-chat/messages/lorUtils';
import { EditorProvider } from '@/components/demands-v2/editor/context/editor-provider';
import { FaxWizard } from '@/components/fax/wizard/FaxWizard';
import { OnlyOfficeEditor } from '@/components/onlyoffice/OnlyOfficeEditor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCoverageAgentThread } from '@/hooks/useCoverageAgentThread';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import {
  useCreateAgentChatThreadMutation,
  useUpdateAgentChatThreadMutation,
} from '@/services/api/agentChatService';
import {
  useGetCoverageQuery,
  useGetCoveragesQuery,
  useLazyGetLorReviewWorkflowStatusQuery,
  useLazyGetUploadLorWorkflowStatusQuery,
  useLorDataUpdatedMutation,
  usePromoteLorSelectionsMutation,
  useSubmitLorAutofillReviewAsyncMutation,
  useUpdateCoverageMutation,
  useUploadLorFileAsyncMutation,
} from '@/services/api/coverageService';
import type { ReadTemplateField , TemplateCategory } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateFieldsQuery,
} from '@/services/api/generated/templatesV2Service';
import {
  useCreateDocumentMutation,
} from '@/services/api/lettersService';
import { useGetMatterQuery } from '@/services/api/mattersService';
import { useSaveDocumentFileMutation } from '@/services/api/onlyofficeDemoService';
import {
  type EditorConfig,
  useLazyGetDocumentEditorConfigQuery,
} from '@/services/api/onlyofficeService';
import { selectMessagesByThread } from '@/services/slices/agentChat';
//
import type { Task, TaskStatus } from '@/services/types/client-intake-types';
// import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';
import type {
  AdjusterDetail,
  ContactHandleSchema,
  CoverageSchema,
} from '@/services/types/coverage-types';
import type {
  DocumentInstance,
  LorDisplayField,
  LoRScalarChoice,
} from '@/services/types/letters-types';
import {
  buildCoverageChoiceMap,
  buildLorDisplayFields,
  DocumentFieldMapping,
} from '@/services/types/letters-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import { extractContactChannels } from '@/services/utils/coverageHandles';
import { RootState } from '@/state/state';

import { FinalizeDetails } from '../hooks/useClaimFilingState';
import { getLatestCoverageTaskByCanonicalType } from '../premises/premisesFlow';
import type { TemplateLite } from '../types';
import { ArchivedTemplateWarning } from './ArchivedTemplateWarning';
import { CheckLorProgressButton } from './CheckLorProgressButton';
//
import { useArchivedTemplateCheck } from './hooks/useArchivedTemplateCheck';
import { useCachedLorPreview } from './hooks/useCachedLorPreview';
import { useLorDocuments, useLorTemplates } from './hooks/useLorData';
import { useLorPreview } from './hooks/usePreview';
import { LorList } from './LorList';
import { LorPreviewDialog } from './LorPreviewDialog';
import { LorTemplateSelector } from './LorTemplateSelector';
import { RequiredLorFieldsPanel } from './RequiredLorFieldsPanel';
import { UploadLorProgressButton } from './UploadLorProgressButton';
import { UploadLorReviewModeModal } from './UploadLorReviewModeModal';

const RESERVED_FIELD_VALUE_KEYS = new Set([
  'lor_review_html',
  'lor_review_rows',
]);

/** Props for `ClaimLorStep`. */
interface ClaimLorStepProps {
  /** Matter id (used for templates, documents, and transcripts). */
  matterId: string;
  /** Coverage id for template selection and merge fields. */
  coverageId: string;
  /** Setter to control the focused field key (used on hover). */
  setActiveFieldKey: (key: string | null) => void;
  /** Final details used to generate the LoR. */
  finalizeDetails: FinalizeDetails;
  /** Update a specific finalize detail field. */
  updateFinalizeDetail: <K extends keyof FinalizeDetails>(
    key: K,
    value: FinalizeDetails[K],
  ) => void;
  /** Persist finalize details to backend. */
  saveFinalizeDetails: () => void;
  /** Client-side map of LoR id to transmission id for tracking. */
  activeFaxTransmissions?: Map<string, string>;
  /** Initiate a fax send for the given LoR id. */
  onSendFax: (lorId: string, faxNumber: string) => void;
  /** Callback when a client-tracked transmission reaches completion. */
  onTransmissionComplete?: (lorId: string) => void;
  /** Global sending flag to disable buttons when firing. */
  isSendingFax?: boolean;
}

type LorFieldValue = string | LoRScalarChoice;

export const ClaimLorStep: React.FC<ClaimLorStepProps> = ({
  matterId,
  coverageId,
  setActiveFieldKey,
  finalizeDetails,
  updateFinalizeDetail,
  saveFinalizeDetails,
  activeFaxTransmissions = new Map<string, string>(),
  onSendFax,
  onTransmissionComplete,
  isSendingFax = false,
}) => {
  // Constants moved to shared constants.ts

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  // Right panel mode: 'pdf' for PDF preview, 'chat' for FinchGPT assistant
  const [rightPanelMode, setRightPanelMode] = useState<'pdf' | 'chat'>('pdf');
  const [isFaxWizardOpen, setIsFaxWizardOpen] = useState<boolean>(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [uploadLorFileAsync, { isLoading: isUploadMutationRunning }] =
    useUploadLorFileAsyncMutation();
  const [getWorkflowStatus] = useLazyGetUploadLorWorkflowStatusQuery();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isProcessingCustomLor, setIsProcessingCustomLor] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [isUploadModeModalOpen, setIsUploadModeModalOpen] = useState(false);
  const [pendingUploadTemplateId, setPendingUploadTemplateId] = useState<
    string | null
  >(null);

  // OnlyOffice editing state
  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const [getDocumentEditorConfig] = useLazyGetDocumentEditorConfigQuery();
  const [isEditorLoading, setIsEditorLoading] = useState(false);

  const [saveDocumentFile] = useSaveDocumentFileMutation();

  // Polling + spinner for cached PDF after editor close
  const pdfPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pdfPollCountRef = useRef(0);
  const pdfSawNullRef = useRef(false);
  const [isRegeneratingPreview, setIsRegeneratingPreview] = useState(false);

  const handleFieldHover = (key: string | null) => {
    setActiveFieldKey(key);
  };
  // Task completion wiring (reuse StepActionHeader logic)
  const { data: tasksResponse } = useGetTasksQuery(
    {
      matter: matterId,
      limit: 200,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [updateTask] = useUpdateTaskMutation();
  const { data: taskStatuses = [] as TaskStatus[] } = useGetTaskStatusesQuery();
  const sendLorTask = useMemo(() => {
    const tasks = (tasksResponse?.results || []) as Task[];
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId,
      CanonicalTaskType.SEND_LOR,
    );
  }, [tasksResponse, coverageId]);

  const markSendLorTaskCompleted = async () => {
    if (!sendLorTask) return;
    try {
      const completedStatus = (taskStatuses as TaskStatus[]).find(
        (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
      );
      await updateTask({
        matterId,
        taskId: sendLorTask.id,
        task: {
          status_id: completedStatus?.id,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
    } catch (e) {
      // eslint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.error('Failed to mark Send LoR task completed', e);
      toast.error('Could not mark Send LoR task completed');
    }
  };

  // Load coverages for the matter to display in LOR list
  const { data: coveragesData } = useGetCoveragesQuery({ matterId });
  const coveragesMap = useMemo(() => {
    const map = new Map<string, CoverageSchema>();
    coveragesData?.forEach((cov) => {
      map.set(cov.id, cov);
    });
    return map;
  }, [coveragesData]);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );

  // FinchGPT assistant thread (lazy-created if missing)
  const [isCoverageAssistantOpen, setIsCoverageAssistantOpen] = useState(false);
  const {
    threadId: coverageThreadId,
    threads: coverageThreads,
    isLoading: isCoverageThreadLoading,
  } = useCoverageAgentThread({
    matterId,
    coverageId,
  });

  const [selectedCoverageThreadId, setSelectedCoverageThreadId] = useState<
    string | null
  >(null);
  const [isRenameThreadDialogOpen, setIsRenameThreadDialogOpen] =
    useState(false);
  const [renameThreadName, setRenameThreadName] = useState('');

  useEffect(() => {
    if (!selectedCoverageThreadId && coverageThreadId) {
      setSelectedCoverageThreadId(coverageThreadId);
      return;
    }
    if (
      selectedCoverageThreadId &&
      coverageThreads.length > 0 &&
      !coverageThreads.some((thread) => thread.id === selectedCoverageThreadId)
    ) {
      setSelectedCoverageThreadId(coverageThreadId ?? null);
    }
  }, [coverageThreadId, coverageThreads, selectedCoverageThreadId]);

  // Get chat messages from Redux to extract LOR review data
  const chatMessages = useSelector((state: RootState) =>
    selectedCoverageThreadId
      ? selectMessagesByThread(state.agentChat, selectedCoverageThreadId)
      : [],
  );

  // Extract field review map from the most recent LOR review in chat messages
  const fieldReviewMap = useMemo(() => {
    if (!chatMessages || chatMessages.length === 0) return {};
    return extractFieldReviewMapFromMessages(chatMessages);
  }, [chatMessages]);

  const [createCoverageThread, createCoverageThreadState] =
    useCreateAgentChatThreadMutation();
  const [updateCoverageThread, updateCoverageThreadState] =
    useUpdateAgentChatThreadMutation();

  const coverageThreadOptions = useMemo(() => {
    const threads = [...coverageThreads];
    if (
      createCoverageThreadState.data &&
      !threads.some(
        (thread) => thread.id === createCoverageThreadState.data?.id,
      )
    ) {
      threads.unshift(createCoverageThreadState.data);
    }
    return threads;
  }, [coverageThreads, createCoverageThreadState.data]);

  const selectedThread = useMemo(() => {
    if (!selectedCoverageThreadId) return null;
    return (
      coverageThreadOptions.find(
        (thread) => thread.id === selectedCoverageThreadId,
      ) || null
    );
  }, [coverageThreadOptions, selectedCoverageThreadId]);

  const handleCreateNewCoverageThread = useCallback(async () => {
    try {
      if (!matterId || !coverageId) return;
      const newThread = await createCoverageThread({
        matterId,
        data: {
          title: 'Coverage FinchGPT Review',
          initial_task: null,
          workspace_id: null,
          coverage_id: coverageId,
        },
      }).unwrap();
      setSelectedCoverageThreadId(newThread.id);
      toast.success('Started new coverage review thread');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create coverage thread', err);
      toast.error('Could not start a new coverage review thread');
    }
  }, [coverageId, createCoverageThread, matterId]);

  const handleOpenRenameThreadDialog = useCallback(() => {
    if (!selectedCoverageThreadId) return;
    setRenameThreadName(selectedThread?.title ?? '');
    setIsRenameThreadDialogOpen(true);
  }, [selectedCoverageThreadId, selectedThread]);

  const handleCloseRenameThreadDialog = useCallback(
    (nextOpen: boolean) => {
      if (updateCoverageThreadState.isLoading) return;
      setIsRenameThreadDialogOpen(nextOpen);
      if (!nextOpen) {
        setRenameThreadName('');
      }
    },
    [updateCoverageThreadState.isLoading],
  );

  const handleRenameThread = useCallback(async () => {
    if (!matterId || !selectedCoverageThreadId) return;
    const trimmed = renameThreadName.trim();
    if (!trimmed) {
      toast.error('Thread name cannot be empty');
      return;
    }
    try {
      await updateCoverageThread({
        matterId,
        threadId: selectedCoverageThreadId,
        data: { title: trimmed },
      }).unwrap();
      toast.success('Thread renamed');
      setIsRenameThreadDialogOpen(false);
      setRenameThreadName('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to rename coverage thread', err);
      toast.error('Could not rename coverage thread');
    }
  }, [
    matterId,
    renameThreadName,
    selectedCoverageThreadId,
    updateCoverageThread,
  ]);

  const handleOpenCoverageAssistant = () => {
    setIsCoverageAssistantOpen(true);
  };

  const handleCloseCoverageAssistant = () => {
    setIsCoverageAssistantOpen(false);
  };

  // Real LoR list: fetch matter documents and filter by LoR template types
  const {
    lorDocs,
    isLoading: isDocsLoading,
    error: docsError,
    refetch: refetchDocs,
  } = useLorDocuments(matterId);

  // Filter lorDocs to only those belonging to (or unlinked from) this coverage
  const lorDocsForCoverage = useMemo(() => {
    return lorDocs.filter(
      (doc) => !doc.coverage_id || doc.coverage_id === coverageId,
    );
  }, [lorDocs, coverageId]);

  // Check if the template used by the current LOR document has been archived
  const {
    isTemplateArchived,
    templateStatus,
    templateName: archivedTemplateName,
    isLoading: isArchiveCheckLoading,
    isError: isArchiveCheckError,
  } = useArchivedTemplateCheck(lorDocsForCoverage);

  // Get the current document instance for the cached preview
  const currentDocInstance = useMemo(() => {
    if (previewDocId) {
      return lorDocs.find((d) => d.id === previewDocId) ?? null;
    }
    // Fall back to the most recent LOR document for this coverage
    return lorDocsForCoverage.length > 0 ? lorDocsForCoverage[0] : null;
  }, [previewDocId, lorDocs, lorDocsForCoverage]);

  // Use the cached preview hook for optimized PDF loading
  const {
    url: cachedPreviewUrl,
    isLoading: isCachedPreviewLoading,
    isCached: isPdfFromCache,
    refresh: refreshCachedPreview,
  } = useCachedLorPreview({
    matterId,
    documentInstance: currentDocInstance,
    autoFetch: rightPanelMode === 'pdf',
  });

  // Clean up PDF polling on unmount
  useEffect(() => {
    return () => {
      if (pdfPollRef.current) clearTimeout(pdfPollRef.current);
    };
  }, []);

  // Track the null transition of cached_pdf_url to detect PDF regeneration.
  // After OnlyOffice saves, the backend clears cached_pdf_url (null) then
  // regenerates it. We wait for the null→truthy transition before stopping.
  useEffect(() => {
    if (!pdfPollRef.current) return; // No active polling

    if (!currentDocInstance?.cached_pdf_url) {
      // Backend has cleared the old preview — regeneration is in progress
      pdfSawNullRef.current = true;
    } else if (pdfSawNullRef.current) {
      // Preview URL is back after being null — regeneration is complete
      clearTimeout(pdfPollRef.current);
      pdfPollRef.current = null;
      pdfPollCountRef.current = 0;
      pdfSawNullRef.current = false;
      setIsRegeneratingPreview(false);
    }
  }, [currentDocInstance?.cached_pdf_url]);

  // Fetch matter (for firmId) and coverage (for template decision)
  const { data: coverage, refetch: refetchCoverage } = useGetCoverageQuery({
    matterId,
    coverageId,
  });
  const [updateCoverage] = useUpdateCoverageMutation();
  const [lorDataUpdated] = useLorDataUpdatedMutation();
  const [promoteLorSelections] = usePromoteLorSelectionsMutation();

  // Templates for firm
  const {
    lorTemplates,
    isLoading: isTemplatesLoading,
    error: templatesError,
    pickDefaultTemplateId,
    representingAttorneyId,
    representingAttorneyName,
  } = useLorTemplates(matterId);

  // Filter available templates by coverage category (1P vs 3P vs 1P Health)
  const coverageFilteredTemplates = useMemo(() => {
    const cat =
      (coverage as { category?: string | null } | null)?.category ?? null;
    if (cat === 'FIRST_PARTY') {
      return lorTemplates.filter(
        (t) => t.category === ('LOR_FIRST_PARTY' satisfies TemplateCategory),
      );
    }
    if (cat === 'FIRST_PARTY_HEALTH') {
      return lorTemplates.filter(
        (t) => t.category === ('LOR_HEALTH' satisfies TemplateCategory),
      );
    }
    if (cat === 'THIRD_PARTY') {
      return lorTemplates.filter(
        (t) => t.category === ('LOR_THIRD_PARTY' satisfies TemplateCategory),
      );
    }
    return lorTemplates;
  }, [coverage, lorTemplates]);

  const {
    displayTemplates,
    attorneyFilterActive,
  }: {
    displayTemplates: TemplateLite[];
    attorneyFilterActive: boolean;
  } = useMemo(() => {
    if (!representingAttorneyId) {
      return {
        displayTemplates: coverageFilteredTemplates,
        attorneyFilterActive: false,
      };
    }

    // Include templates that match the representing attorney OR have no
    // attorney assigned.  Previously templates without an attorney were
    // excluded when at least one attorney-specific template existed, which
    // unintentionally hid generic firm-wide templates.
    const relevantTemplates = coverageFilteredTemplates.filter(
      (t) => !t.attorney_id || t.attorney_id === representingAttorneyId,
    );

    if (relevantTemplates.length > 0) {
      return {
        displayTemplates: relevantTemplates,
        attorneyFilterActive: true,
      };
    }

    return {
      displayTemplates: coverageFilteredTemplates,
      attorneyFilterActive: false,
    };
  }, [coverageFilteredTemplates, representingAttorneyId]);

  // Default selection respecting coverage + attorney filters
  useEffect(() => {
    if (!coverage) return;
    if (!displayTemplates.length) {
      setSelectedTemplateId(null);
      return;
    }
    const cat =
      (coverage as { category?: string | null } | null)?.category ?? null;
    setSelectedTemplateId((current) => {
      if (current && displayTemplates.some((t) => t.id === current)) {
        return current;
      }
      const fallback = pickDefaultTemplateId(cat, displayTemplates);
      return fallback ?? displayTemplates[0]?.id ?? null;
    });
  }, [coverage, displayTemplates, pickDefaultTemplateId]);

  // Fetch firm id and template fields for selected template
  const { data: matterForFirm } = useGetMatterQuery(matterId);
  const firmIdForFields = matterForFirm?.firm?.id || null;
  const { data: rawTemplateFields, isLoading: isTemplateFieldsLoading } =
    useTemplateFieldsQuery(
      selectedTemplateId
        ? { id: selectedTemplateId, matterId, coverageId }
        : (skipToken as unknown as { id: string }),
      {
        refetchOnMountOrArgChange: true,
      },
    );

  // Build choice arrays from coverage data for dropdown fields
  const choiceMap = useMemo(
    () => buildCoverageChoiceMap(coverage, coverageId),
    [coverage, coverageId],
  );

  // Map v2 template fields to LorDisplayField[] for the RequiredLorFieldsPanel.
  // Choice arrays (adjuster names, policy numbers, etc.) are derived from
  // coverage data and injected into field.default_value so downstream
  // consumers can render dropdowns.
  const templateFields = useMemo(() => {
    if (!rawTemplateFields) return undefined;
    const v2Fields = rawTemplateFields as unknown as ReadTemplateField[];
    return buildLorDisplayFields(v2Fields, choiceMap);
  }, [rawTemplateFields, choiceMap]);

  // ==========================================================================
  // FIELD VALUES - Single source of truth for what's displayed in the boxes
  // ==========================================================================
  // fieldValues holds the actual values shown in the UI.
  // When user edits a field, fieldValues is updated directly.
  // When we submit, we read from fieldValues - no complex priority logic.
  // ==========================================================================

  const [fieldValues, setFieldValues] = useState<Record<string, LorFieldValue>>(
    {},
  );

  // Track which fields user has explicitly touched (to preserve their edits)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Helper to get canonical key for a template field
  const getFieldKey = (tf: LorDisplayField): string => {
    const ref = (tf.reference || '').trim();
    if (ref) return ref;
    return tf.id;
  };

  // Compute default values - SIMPLE:
  //   1. Backend default_value (if array, auto-select first choice)
  //   2. Empty string fallback
  const computedDefaults: Record<string, LorFieldValue> = useMemo(() => {
    const result: Record<string, LorFieldValue> = {};
    const fields = templateFields?.fields || [];

    fields.forEach((f) => {
      const key = getFieldKey(f);
      const backend = f.default_value as unknown;

      // If backend sends an array of choices, auto-select the first one
      if (Array.isArray(backend) && backend.length > 0) {
        result[key] = backend[0] as LorFieldValue;
        return;
      }

      // If backend sends a single meaningful value, use it
      if (
        backend !== null &&
        backend !== undefined &&
        (typeof backend !== 'string' || backend.trim() !== '')
      ) {
        result[key] = backend as LorFieldValue;
        return;
      }

      // Empty string fallback
      result[key] = '';
    });

    return result;
  }, [templateFields]);

  // Initialize/update fieldValues from computed defaults
  // Only update fields that user hasn't touched
  useEffect(() => {
    const fields = templateFields?.fields || [];
    if (!fields.length) return;

    setFieldValues((prev) => {
      const next = { ...prev };
      fields.forEach((f) => {
        const key = getFieldKey(f);
        // Only set default if field hasn't been touched by user
        if (!touchedFields.has(key)) {
          next[key] = computedDefaults[key] ?? '';
        }
      });
      return next;
    });
  }, [computedDefaults, templateFields, touchedFields]);

  // Track whether we've loaded from existing LOR to prevent re-loading
  const [hasLoadedFromExistingLor, setHasLoadedFromExistingLor] =
    useState(false);

  // Load field values from the most recent LOR document on page load
  useEffect(() => {
    // Skip if already loaded or if data isn't ready
    if (hasLoadedFromExistingLor || isDocsLoading || !coverage) return;

    const existingLorDocId = coverage.lor_document_id;
    if (!existingLorDocId) {
      setHasLoadedFromExistingLor(true);
      return;
    }

    // Find the document in lorDocs
    const existingDoc = lorDocs.find((d) => d.id === existingLorDocId);
    if (!existingDoc) {
      // Document might not be in the list yet, wait for next render
      return;
    }

    // Set the template from the existing document
    if (existingDoc.document_template?.id) {
      setSelectedTemplateId(existingDoc.document_template.id);
    }

    // Extract and load merge_fields from the existing document
    const edits = extractFieldEditsFromDocument(
      existingDoc.merge_fields as Record<string, unknown> | null | undefined,
    );
    if (Object.keys(edits).length > 0) {
      setFieldValues((prev) => ({ ...prev, ...edits }));
    }

    setHasLoadedFromExistingLor(true);
  }, [coverage, lorDocs, isDocsLoading, hasLoadedFromExistingLor]);

  // Legacy alias for compatibility - points to same data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const lorFieldEdits = fieldValues;
  const setLorFieldEdits = (
    updater:
      | Record<string, LorFieldValue>
      | ((
          prev: Record<string, LorFieldValue>,
        ) => Record<string, LorFieldValue>),
  ) => {
    if (typeof updater === 'function') {
      setFieldValues((prev) => {
        const next = updater(prev);
        // Mark all changed keys as touched
        Object.keys(next).forEach((key) => {
          if (prev[key] !== next[key]) {
            setTouchedFields((t) => new Set(t).add(key));
          }
        });
        return next;
      });
    } else {
      Object.keys(updater).forEach((key) => {
        setTouchedFields((t) => new Set(t).add(key));
      });
      setFieldValues(updater);
    }
  };

  // requiredFieldValues is now just fieldValues - single source of truth
  const requiredFieldValues = fieldValues;

  // Contact method options are computed after resolving fallback handles

  // Fallbacks from coverage/matter defaults (display-only unless user updates)
  const coerceFieldValue = (val: unknown): LorFieldValue | undefined => {
    if (val === null || val === undefined) return undefined;
    if (typeof val === 'string') return val;
    if (
      typeof val === 'object' &&
      !Array.isArray(val) &&
      Object.prototype.hasOwnProperty.call(
        val as Record<string, unknown>,
        'value',
      )
    ) {
      return val as LoRScalarChoice;
    }
    return undefined;
  };

  const toDisplayString = (value: LorFieldValue | undefined): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') return value.value ?? '';
    return '';
  };

  const isScalarChoiceValue = (value: unknown): value is LoRScalarChoice =>
    Boolean(
      value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'value' in (value as Record<string, unknown>),
    );

  // Note: Field initialization is now handled by the single useEffect above
  // that syncs computedDefaults → fieldValues for untouched fields

  // Prefer first adjuster only (per product decision) and extract contact channels
  const primaryAdjuster = Array.isArray(coverage?.adjusters)
    ? ((coverage?.adjusters as AdjusterDetail[])[0] as AdjusterDetail)
    : undefined;
  const fallbackAdjName = primaryAdjuster?.name ?? '';
  const adjHandles = Array.isArray(primaryAdjuster?.handles) ? primaryAdjuster.handles : [];
  const { email: fallbackEmail, phone: fallbackPhone, fax: fallbackFax } =
    extractContactChannels(adjHandles as ContactHandleSchema[]);

  // Carrier handles fallback for email/phone/fax (preferred for LoR and follow-ups)
  const carrierHandles = Array.isArray(coverage?.carrier?.handles) ? coverage.carrier.handles : [];
  const {
    email: fallbackCarrierEmail,
    phone: fallbackCarrierPhone,
    fax: fallbackCarrierFax,
  } = extractContactChannels(carrierHandles as ContactHandleSchema[]);

  // Now that fallbackEmail/fax are known, compute Contact Method options and selection
  // Priority: EMAIL > FAX (email is preferred; carrier EMAIL beats adjuster FAX)
  const contactMethodOptions = useMemo(() => {
    const opts = new Set<string>();
    // Include both adjuster-level and carrier-level handles
    const hasEmail =
      finalizeDetails.adjusterEmail || fallbackEmail || fallbackCarrierEmail;
    const hasFax =
      finalizeDetails.adjusterFax || fallbackFax || fallbackCarrierFax;
    if (hasEmail) opts.add('EMAIL');
    if (hasFax) opts.add('FAX');
    const rank = (method: string) => {
      if (method === 'EMAIL') return 0;
      if (method === 'FAX') return 1;
      return 2;
    };
    return Array.from(opts).sort((a, b) => rank(a) - rank(b));
  }, [
    finalizeDetails.adjusterFax,
    finalizeDetails.adjusterEmail,
    fallbackFax,
    fallbackEmail,
    fallbackCarrierEmail,
    fallbackCarrierFax,
  ]);

  // Keep default contact method in state where needed; value derived on demand elsewhere

  // Auto-select contact method when options change
  // Priority: EMAIL > FAX (email is preferred contact method)
  useEffect(() => {
    setLorFieldEdits((prev) => {
      const current = prev[DocumentFieldMapping.CONTACT_METHOD];
      const currentStr = typeof current === 'string' ? current : undefined;
      let nextVal: string | undefined = currentStr;
      if (!currentStr || !contactMethodOptions.includes(currentStr)) {
        if (contactMethodOptions.includes('EMAIL')) nextVal = 'EMAIL';
        else if (contactMethodOptions.includes('FAX')) nextVal = 'FAX';
        else nextVal = undefined;
      }
      if (nextVal === currentStr) return prev;
      const next = { ...prev } as Record<string, LorFieldValue>;
      if (nextVal) next[DocumentFieldMapping.CONTACT_METHOD] = nextVal;
      else delete next[DocumentFieldMapping.CONTACT_METHOD];
      return next;
    });
  }, [contactMethodOptions]);

  // Build merge fields - simply read from fieldValues (single source of truth)
  const buildMergeFields = (): Record<string, string> => {
    const merged: Record<string, string> = {};

    // Convert fieldValues to string values
    Object.entries(fieldValues).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        merged[key] = '';
        return;
      }
      if (typeof value === 'string') {
        merged[key] = value;
        return;
      }
      if (isScalarChoiceValue(value)) {
        merged[key] = value.value ?? '';
      }
    });

    // Derive transmission/recipient camelCase fields from the user's
    // explicit contact method + carrier selection so both old templates
    // ({{ contact_method }}: {{ adjuster_contact }}) and new templates
    // ({{ transmissionMethod }}, {{ recipientName }}) render correctly.
    const contactMethod = merged[DocumentFieldMapping.CONTACT_METHOD] || '';
    const adjusterContact =
      merged[DocumentFieldMapping.ADJUSTER_CONTACT] || '';
    if (contactMethod && adjusterContact && !merged.transmissionMethod) {
      const methodLabel =
        contactMethod === 'EMAIL'
          ? 'Via Email'
          : contactMethod === 'FAX'
            ? 'Via Facsimile'
            : contactMethod;
      merged.transmissionMethod = `${methodLabel}: ${adjusterContact}`;
    }
    if (!merged.transmissionDate) {
      merged.transmissionDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      });
    }
    const carrierName = merged[DocumentFieldMapping.CARRIER_NAME] || '';
    if (carrierName && !merged.recipientName) {
      merged.recipientName = carrierName;
    }
    const carrierAddr1 =
      merged[DocumentFieldMapping.CARRIER_ADDRESS_1] || '';
    if (carrierAddr1 && !merged.recipientAddress1) {
      merged.recipientAddress1 = carrierAddr1;
    }
    const carrierAddr2 =
      merged[DocumentFieldMapping.CARRIER_ADDRESS_2] || '';
    if (carrierAddr2 && !merged.recipientAddress2) {
      merged.recipientAddress2 = carrierAddr2;
    }

    // Add template field aliases (UPPERCASE) for backend compatibility
    const fieldsForTemplate = (templateFields?.fields || []) as LorDisplayField[];

    fieldsForTemplate.forEach((tf) => {
      const canonicalKey = (tf.reference || '').trim() || tf.id;
      if (!canonicalKey) return;

      const canonicalValue = merged[canonicalKey];
      if (canonicalValue === undefined) return;

      const upperAlias = canonicalKey.toUpperCase();
      if (!(upperAlias in merged)) {
        merged[upperAlias] = canonicalValue;
      }
    });

    return merged;
  };

  const [createDocument, { isLoading: isCreating }] =
    useCreateDocumentMutation();
  const [submitLorAutofillReviewAsync] =
    useSubmitLorAutofillReviewAsyncMutation();
  const [getLorReviewWorkflowStatus] = useLazyGetLorReviewWorkflowStatusQuery();
  const [isCheckingLor, setIsCheckingLor] = useState(false);
  const [checkLorProgress, setCheckLorProgress] = useState<string>('');

  const handleViewLor = (id: string) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDocId(id);
    setIsPreviewOpen(true);
  };

  const handleEditDocument = async () => {
    const docId = currentDocInstance?.id;
    if (!docId || !matterId) return;
    setIsEditorLoading(true);
    try {
      const config = await getDocumentEditorConfig({
        matterId,
        documentId: docId,
      }).unwrap();
      setEditorConfig(config);
    } catch {
      toast.error('Failed to load document editor');
    } finally {
      setIsEditorLoading(false);
    }
  };

  /**
   * Start polling refetchDocs every 2s. The reactive useCachedLorPreview hook
   * will update the preview automatically once cached_pdf_url appears in the
   * refetched data. Polling is stopped by the useEffect above.
   */
  const startPdfPolling = () => {
    if (pdfPollRef.current) clearTimeout(pdfPollRef.current);
    pdfPollCountRef.current = 0;

    const poll = () => {
      pdfPollCountRef.current += 1;
      pdfPollRef.current = setTimeout(async () => {
        await refetchDocs();
        if (pdfPollCountRef.current < 15) {
          poll();
        } else {
          pdfPollRef.current = null;
          setIsRegeneratingPreview(false);
        }
      }, 2000);
    };
    poll();
  };

  const handleEditorClose = () => {
    setEditorConfig(null);
    setIsRegeneratingPreview(true);
    pdfSawNullRef.current = false;
    // The OnlyOffice callback fires asynchronously after the editor unmounts.
    // Poll refetchDocs until the backend regenerates cached_pdf_url;
    // the reactive useCachedLorPreview hook updates the preview automatically.
    startPdfPolling();
  };

  const handleLoadLorFromList = (id: string) => {
    const doc = lorDocs.find((d) => d.id === id);
    if (!doc) {
      toast.error('LoR not found for this coverage');
      return;
    }
    // Set the template
    if (doc.document_template?.id) {
      setSelectedTemplateId(doc.document_template.id);
    }
    // Use merge_fields (original user input) instead of field_values (which may have metadata)
    const edits = extractFieldEditsFromDocument(
      doc.merge_fields as Record<string, unknown> | null | undefined,
    );
    const hasEdits = Object.keys(edits).length > 0;
    if (hasEdits) {
      setLorFieldEdits(edits);
    }
    // Set the preview doc ID (this also triggers the cached preview hook)
    setPreviewDocId(id);
    // Switch to PDF view mode to show the loaded document
    setRightPanelMode('pdf');
    toast[hasEdits ? 'success' : 'info'](
      hasEdits
        ? 'Loaded fields from selected LoR'
        : 'Loaded LoR preview (no stored merge fields to import)',
    );
  };

  const submitLorReviewRequest = async (
    template: TemplateLite | undefined,
    mergeFields: Record<string, string>,
    documentId: string | null,
  ) => {
    setIsCheckingLor(true);
    setCheckLorProgress('PENDING');

    try {
      const result = await submitLorAutofillReviewAsync({
        matterId,
        coverageId,
        body: {
          template_id: template?.id ?? null,
          template_name: template?.name ?? null,
          merge_fields: mergeFields,
          document_id: documentId,
          thread_id: selectedCoverageThreadId ?? null,
        },
      }).unwrap();

      const workflowId = result.workflow_id;

      // Poll for workflow status
      const pollInterval = 1500;
      const maxAttempts = 120; // 3 minutes max
      let attempts = 0;

      let errorCount = 0;
      const maxErrors = 3;

      const poll = async (): Promise<boolean> => {
        if (attempts >= maxAttempts) {
          setCheckLorProgress('FAILED');
          toast.error('Review timed out. Please try again.');
          return false;
        }
        attempts += 1;

        try {
          const statusResult = await getLorReviewWorkflowStatus({
            matterId,
            coverageId,
            workflowId,
          }).unwrap();

          // Reset error count on successful poll
          errorCount = 0;

          // Normalize status to uppercase for progress tracking
          const normalizedStatus =
            statusResult.status?.toUpperCase() || 'REVIEWING';
          setCheckLorProgress(normalizedStatus);

          if (!statusResult.is_running) {
            const success = normalizedStatus === 'COMPLETED';
            if (success) {
              toast.success('Sent to FinchGPT for review');
            } else if (statusResult.error_message) {
              toast.error(`Review failed: ${statusResult.error_message}`);
            }
            return success;
          }

          // Continue polling
          await new Promise<void>((resolve) => {
            setTimeout(resolve, pollInterval);
          });
          return await poll();
        } catch (err) {
          console.error('Failed to poll workflow status', err);
          errorCount += 1;

          // Stop polling after too many consecutive errors
          if (errorCount >= maxErrors) {
            // Assume it completed if we can't reach it anymore
            setCheckLorProgress('COMPLETED');
            toast.success('Sent to FinchGPT for review');
            return true;
          }

          // Continue polling on transient errors
          await new Promise<void>((resolve) => {
            setTimeout(resolve, pollInterval);
          });
          return await poll();
        }
      };

      await poll();
    } catch (err) {
      console.error('Failed to start LOR review workflow', err);
      setCheckLorProgress('FAILED');
      throw err;
    } finally {
      setIsCheckingLor(false);
      // Clear progress after a short delay to show completion state
      setTimeout(() => setCheckLorProgress(''), 2000);
    }
  };

  const ensureCoverageMergeField = (
    mergeFields: Record<string, string>,
  ): Record<string, string> => {
    if (coverageId && !mergeFields.coverage_id) {
      return { ...mergeFields, coverage_id: coverageId };
    }
    return mergeFields;
  };

  const createDocumentSnapshotForReview = async (
    templateId: string,
    mergeFields: Record<string, string>,
  ): Promise<DocumentInstance | null> => {
    try {
      const doc = await createDocument({
        matterId,
        data: {
          document_template_id: templateId,
          coverage_id: coverageId,
          merge_fields: mergeFields,
          allow_duplicate: true,
        },
      }).unwrap();
      return doc;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to capture LoR review snapshot', err);
      return null;
    }
  };

  const extractFieldEditsFromDocument = (
    documentFieldValues: Record<string, unknown> | null | undefined,
  ): Record<string, LorFieldValue> => {
    if (!documentFieldValues) {
      return {};
    }
    const next: Record<string, LorFieldValue> = {};
    Object.entries(documentFieldValues).forEach(([key, value]) => {
      if (!key || RESERVED_FIELD_VALUE_KEYS.has(key)) {
        return;
      }
      if (value === null || value === undefined) {
        return;
      }
      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'value' in (value as Record<string, unknown>)
      ) {
        next[key] = value as LorFieldValue;
        return;
      }
      next[key] = String(value);
    });
    return next;
  };

  const filterMergeFieldsForTemplate = (
    mergeFields: Record<string, string>,
  ): Record<string, string> => {
    const fields = (templateFields?.fields || []) as LorDisplayField[];
    if (!fields.length) {
      return mergeFields;
    }

    type FieldLookup = {
      preferredKey: string;
      aliases: Set<string>;
    };

    const lookups: FieldLookup[] = fields.map((field) => {
      const ref = (field.reference || '').trim();
      const fieldId = field.id;

      const preferredKey = ref || fieldId;
      const aliases = new Set<string>();

      [ref, fieldId]
        .filter((key) => key && key.length > 0)
        .forEach((key) => {
          aliases.add(key);
          aliases.add(key.toUpperCase());
        });

      return { preferredKey, aliases };
    });

    const filtered: Record<string, string> = {};
    lookups.forEach(({ preferredKey, aliases }) => {
      if (!preferredKey) {
        return;
      }
      const match = Object.keys(mergeFields).find(
        (key) => aliases.has(key) || aliases.has(key.toUpperCase()),
      );
      if (match) {
        filtered[preferredKey] = mergeFields[match];
      }
    });

    return filtered;
  };

  /**
   * Check LoR (audit only) — sends the EXISTING document to FinchGPT for review
   * without regenerating it from the template. This preserves any edits made in
   * OnlyOffice.
   */
  const handleCheckLorOnly = async () => {
    if (!selectedCoverageThreadId) {
      toast.error(
        'No chat thread selected. Please wait for the thread to load or select one.',
      );
      return;
    }
    const tpl = displayTemplates.find((t) => t.id === selectedTemplateId);

    // Use the currently loaded document (from OnlyOffice edits or prior generation)
    const existingDocId = currentDocInstance?.id ?? previewDocId ?? null;
    if (!existingDocId) {
      toast.error(
        'No existing LoR document to check. Use "Generate & Check" to create one first.',
      );
      return;
    }

    const mergeFields = ensureCoverageMergeField(buildMergeFields());
    try {
      const filteredMergeFields = filterMergeFieldsForTemplate(mergeFields);
      await submitLorReviewRequest(tpl, filteredMergeFields, existingDocId);
      toast.success('Sent to FinchGPT for review');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit LoR review request', err);
      toast.error('Could not start FinchGPT review');
    }
  };

  /**
   * Generate & Check LoR — regenerates the document from the template + merge fields,
   * then sends it to FinchGPT for review. This overwrites any OnlyOffice edits.
   */
  const handleGenerateAndCheckLor = async () => {
    if (!selectedTemplateId) {
      toast.error('Select a template before running the FinchGPT check');
      return;
    }
    if (!selectedCoverageThreadId) {
      toast.error(
        'No chat thread selected. Please wait for the thread to load or select one.',
      );
      return;
    }
    const tpl = displayTemplates.find((t) => t.id === selectedTemplateId);
    if (!tpl) {
      toast.error('Unable to find the selected template');
      return;
    }
    const mergeFields = ensureCoverageMergeField(buildMergeFields());
    try {
      const doc = await createDocumentSnapshotForReview(tpl.id, mergeFields);
      if (!doc) {
        toast.error('Could not capture LoR snapshot for review');
        return;
      }
      await refetchDocs();
      const filteredMergeFields = filterMergeFieldsForTemplate(mergeFields);
      await submitLorReviewRequest(tpl, filteredMergeFields, doc.id);
      toast.success('Sent to FinchGPT for review');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit LoR review request', err);
      toast.error('Could not start FinchGPT review');
    }
  };

  // Helpers to persist per-field edits back to Coverage using promotion logic.
  // The backend promotes values to position 0 without creating duplicates.
  const persistPolicyNumber = async (next: string) => {
    try {
      await promoteLorSelections({
        matterId,
        coverageId,
        policyNumber: next,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event
      try {
        await lorDataUpdated({
          matterId,
          coverageId,
          body: {
            document_id: previewDocId || null,
            mapping: DocumentFieldMapping.POLICY_NUMBER,
            old_value: undefined,
            new_value: next,
          },
        }).unwrap();
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist policy number', e);
      toast.error('Failed to save policy number');
    }
  };

  const persistClaimNumber = async (next: string) => {
    try {
      await promoteLorSelections({
        matterId,
        coverageId,
        claimNumber: next,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event
      try {
        await lorDataUpdated({
          matterId,
          coverageId,
          body: {
            document_id: previewDocId || null,
            mapping: DocumentFieldMapping.CLAIM_NUMBER,
            old_value: undefined,
            new_value: next,
          },
        }).unwrap();
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist claim number', e);
      toast.error('Failed to save claim number');
    }
  };

  /**
   * Resolve adjuster field values from finalize details with optional overrides.
   * Used to determine what values to send to the promotion endpoint.
   */
  const resolveAdjusterFields = (
    override?: Partial<typeof finalizeDetails>,
  ) => {
    const resolveField = <K extends keyof FinalizeDetails>(
      key: K,
      fallbackValue: FinalizeDetails[K],
    ): FinalizeDetails[K] => {
      if (override && Object.prototype.hasOwnProperty.call(override, key)) {
        const overrideValue = override[key];
        return (
          overrideValue === undefined ? finalizeDetails[key] : overrideValue
        ) as FinalizeDetails[K];
      }
      return finalizeDetails[key] ?? fallbackValue;
    };

    const resolvedName =
      resolveField('adjusterName', '') || fallbackAdjName || null;
    const resolvedEmail =
      resolveField('adjusterEmail', '') || fallbackEmail || '';
    const resolvedPhone =
      resolveField('adjusterPhone', '') || fallbackPhone || '';
    const resolvedFax = resolveField('adjusterFax', '') || fallbackFax || '';

    return {
      name: resolvedName || undefined,
      email: resolvedEmail || undefined,
      phone: resolvedPhone || undefined,
      fax: resolvedFax || undefined,
    };
  };

  const persistAdjuster = async (
    override?: Partial<typeof finalizeDetails>,
  ) => {
    try {
      const resolved = resolveAdjusterFields(override);
      await promoteLorSelections({
        matterId,
        coverageId,
        adjusterName: resolved.name,
        adjusterEmail: resolved.email,
        adjusterPhone: resolved.phone,
        adjusterFax: resolved.fax,
      }).unwrap();
      saveFinalizeDetails();
      // Emit audit event(s) for the specific override keys saved
      try {
        if (override?.adjusterName !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: DocumentFieldMapping.ADJUSTER_NAME,
              old_value: undefined,
              new_value: override.adjusterName || '',
            },
          }).unwrap();
        }
        if (override?.adjusterEmail !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_EMAIL',
              old_value: undefined,
              new_value: override.adjusterEmail || '',
            },
          }).unwrap();
        }
        if (override?.adjusterFax !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_FAX',
              old_value: undefined,
              new_value: override.adjusterFax || '',
            },
          }).unwrap();
        }
        if (override?.adjusterPhone !== undefined) {
          await lorDataUpdated({
            matterId,
            coverageId,
            body: {
              document_id: previewDocId || null,
              mapping: 'ADJUSTER_PHONE',
              old_value: undefined,
              new_value: override.adjusterPhone || '',
            },
          }).unwrap();
        }
      } catch (err) {
        // Intentionally ignore audit emission failures
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist adjuster details', e);
      toast.error('Failed to save adjuster details');
    }
  };

  const handleViewLorButton = () => {
    const preferredDocId =
      lorDocsForCoverage[0]?.id || previewDocId || coverage?.lor_document_id || null;
    if (!preferredDocId) {
      toast.error('Generate a Letter of Representation first via Check LoR.');
      return;
    }
    handleViewLor(preferredDocId);
  };

  const formatApiError = useCallback((error: unknown, fallback: string) => {
    if (error && typeof error === 'object' && 'data' in error) {
      const { data } = error as { data?: { error?: string; detail?: string } };
      if (data?.error && typeof data.error === 'string') return data.error;
      if (data?.detail && typeof data.detail === 'string') return data.detail;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }, []);

  const pollWorkflowStatus = useCallback(
    async (workflowId: string): Promise<boolean> => {
      const POLL_INTERVAL_MS = 250;
      const MAX_POLLS = 20000;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 3;
      let sawProgress = false; // Track if we ever saw workflow making progress

      /* eslint-disable no-await-in-loop -- Intentional sequential polling */
      for (let i = 0; i < MAX_POLLS; i += 1) {
        try {
          if (i > 0) {
            await new Promise((resolve) => {
              setTimeout(resolve, POLL_INTERVAL_MS);
            });
          }

          const result = await getWorkflowStatus({
            matterId,
            coverageId,
            workflowId,
          }).unwrap();

          // Reset error counter on successful poll
          consecutiveErrors = 0;
          sawProgress = true; // We got a valid response, workflow is running

          // Use status enum for progress tracking (UploadLorProgressButton maps it)
          // Backend returns lowercase status values (e.g., "reviewing", "completed")
          const normalizedStatus = result.status?.toUpperCase() || '';
          setUploadProgress(normalizedStatus);

          if (!result.is_running) {
            if (normalizedStatus === 'COMPLETED') {
              return true;
            }
            if (normalizedStatus === 'FAILED') {
              throw new Error(result.error_message || 'Upload workflow failed');
            }
            // Workflow finished but not COMPLETED or FAILED - treat as success
            return true;
          }
        } catch (error) {
          consecutiveErrors += 1;
          // eslint-disable-next-line no-console
          console.warn(
            `Workflow poll error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`,
            error,
          );

          // Re-throw workflow failure errors
          if (
            error instanceof Error &&
            (error.message === 'Upload workflow failed' ||
              error.message.includes('Upload workflow failed'))
          ) {
            throw error;
          }

          // After consecutive errors, check if we saw progress
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            if (sawProgress) {
              // Workflow was running and then disappeared - assume it completed
              setUploadProgress('COMPLETED');
              return true;
            }
            throw new Error(
              'Lost connection to workflow - please check if upload completed',
            );
          }
        }
      }
      /* eslint-enable no-await-in-loop */

      throw new Error('Upload timed out - please try again');
    },
    [coverageId, getWorkflowStatus, matterId],
  );

  const handleUploadLorChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !matterId || !coverageId) {
        event.target.value = '';
        return;
      }
      setIsProcessingCustomLor(true);
      setUploadProgress('PENDING');
      try {
        const result = await uploadLorFileAsync({
          matterId,
          coverageId,
          file,
          threadId: selectedCoverageThreadId ?? undefined,
          templateId: pendingUploadTemplateId,
        }).unwrap();
        toast.success('Uploaded LoR file');

        // Poll for workflow completion - the workflow handles all steps including AI review
        const success = await pollWorkflowStatus(result.workflow_id);

        // Show completion status briefly before hiding progress
        setUploadProgress(success ? 'COMPLETED' : 'FAILED');

        if (success) {
          toast.success('Uploaded and reviewed LoR file');
          // Brief delay so user sees the 100% completion state
          await new Promise((resolve) => {
            setTimeout(resolve, 800);
          });
          await refetchCoverage();
          await refetchDocs();
        } else {
          toast.warning('Upload completed but review may have issues');
          await new Promise((resolve) => {
            setTimeout(resolve, 800);
          });
          await refetchCoverage();
          await refetchDocs();
        }
      } catch (error) {
        toast.error(formatApiError(error, 'Failed to upload LoR file'));
      } finally {
        setIsProcessingCustomLor(false);
        setUploadProgress('');
        setPendingUploadTemplateId(null);
        event.target.value = '';
      }
    },
    [
      coverageId,
      formatApiError,
      matterId,
      pendingUploadTemplateId,
      pollWorkflowStatus,
      refetchCoverage,
      refetchDocs,
      uploadLorFileAsync,
      selectedCoverageThreadId,
    ],
  );

  const handleUploadLorButtonClick = useCallback(() => {
    // Open the review mode selection modal - file picker opens after user confirms
    setIsUploadModeModalOpen(true);
  }, []);

  const handleUploadModeConfirm = useCallback((templateId: string | null) => {
    // Store the selected template ID and trigger file input
    setPendingUploadTemplateId(templateId);
    setIsUploadModeModalOpen(false);
    // Use setTimeout to ensure modal closes before file picker opens
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  }, []);

  const isCustomLorBusy = isProcessingCustomLor || isUploadMutationRunning;

  const {
    url: previewUrlHook,
    filename: previewFilename,
    isLoading: isPreviewLoading,
    request: requestPreview,
  } = useLorPreview(matterId, previewDocId);

  const backendHost =
    (
      import.meta as unknown as {
        env?: { VITE_APP_DJANGO_BACKEND_HOST?: string };
      }
    ).env?.VITE_APP_DJANGO_BACKEND_HOST || '';

  const checkLorDisabled =
    isTemplatesLoading ||
    !selectedTemplateId ||
    isCheckingLor ||
    isCreating ||
    isCoverageThreadLoading ||
    !selectedCoverageThreadId;

  // Evidence keys to compute across transcripts: include finalize keys and LOR field keys

  if (editorConfig) {
    return (
      <OnlyOfficeEditor
        editorConfig={editorConfig}
        onClose={handleEditorClose}
      />
    );
  }

  return (
    <div className="mx-auto w-full h-[calc(100vh-8rem)] space-y-6 px-4 lg:px-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start h-full">
        <div className="flex-[3] space-y-6 overflow-y-auto h-full pr-2">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            {!(
              coverage?.carrier?.handles && coverage.carrier.handles.length > 0
            ) && (
              <div className="rounded border border-red-200 bg-red-50 p-3 mb-3">
                <div className="text-xs font-semibold text-red-800">
                  Missing carrier contact
                </div>
                <div className="text-[11px] text-red-700 mt-1 space-y-1">
                  <p>
                    Attach a carrier contact handle (phone, email, fax, website)
                    to this carrier.
                  </p>
                  <p>
                    Use the Carrier Directory to select the correct division and
                    handles.
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
              <div className="flex-1 min-w-0">
                <LorTemplateSelector
                  templates={displayTemplates}
                  selectedTemplateId={selectedTemplateId}
                  isLoading={isTemplatesLoading}
                  onChange={(v) => setSelectedTemplateId(v)}
                  filterActive={attorneyFilterActive}
                  filterLabel={representingAttorneyName ?? undefined}
                />
              </div>
              {displayTemplates.length > 0 && (
                <div className="w-full md:w-48 flex flex-col gap-2">
                  <Button
                    className="w-full"
                    onClick={handleViewLorButton}
                    disabled={
                      !(
                        lorDocsForCoverage.length > 0 ||
                        coverage?.lor_document_id ||
                        previewDocId
                      )
                    }
                  >
                    View LoR
                  </Button>
                  <CheckLorProgressButton
                    onClick={handleCheckLorOnly}
                    disabled={
                      checkLorDisabled ||
                      !(currentDocInstance?.id || previewDocId)
                    }
                    isProcessing={isCheckingLor}
                    currentStatus={checkLorProgress}
                    label="Check LoR"
                  />
                  <CheckLorProgressButton
                    onClick={handleGenerateAndCheckLor}
                    disabled={checkLorDisabled}
                    isProcessing={isCheckingLor}
                    currentStatus={checkLorProgress}
                    label="Generate & Check"
                  />
                  <UploadLorProgressButton
                    onClick={handleUploadLorButtonClick}
                    disabled={isCustomLorBusy && !isProcessingCustomLor}
                    isProcessing={isProcessingCustomLor}
                    currentStatus={uploadProgress}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    onChange={handleUploadLorChange}
                  />
                  <Button
                    variant="ghost"
                    className="w-full text-zinc-500"
                    asChild
                  >
                    <Link
                      to={`/matters/${matterId}/coverage/${coverageId}/draft-lor`}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Use legacy drafter
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* AI Source Indicator when document has field values from AI */}
          {currentDocInstance?.field_values_source === 'system' && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-center gap-3">
              <div className="flex-shrink-0 p-2 rounded-lg bg-violet-100">
                <svg
                  className="h-5 w-5 text-violet-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-violet-800">
                  AI-Generated Field Values
                </p>
                <p className="text-xs text-violet-600">
                  FinchGPT populated these fields based on coverage data. Review
                  and edit as needed.
                </p>
              </div>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <RequiredLorFieldsPanel
              fields={(templateFields?.fields || []) as LorDisplayField[]}
              values={requiredFieldValues}
              isLoading={isTemplateFieldsLoading}
              onHover={handleFieldHover}
              fieldReviewMap={fieldReviewMap}
              onDraftChange={(mapping, value) => {
                const fieldValue = value as LorFieldValue;
                if (mapping === DocumentFieldMapping.ADJUSTER_CONTACT) {
                  let method: string | undefined;
                  if (isScalarChoiceValue(fieldValue)) {
                    method = fieldValue.context?.contact_handle?.handle_type;
                  }
                  setLorFieldEdits((prev) => ({
                    ...prev,
                    [mapping]: fieldValue,
                    ...(method && {
                      [DocumentFieldMapping.CONTACT_METHOD]: method,
                    }),
                  }));
                } else {
                  setLorFieldEdits((prev) => ({
                    ...prev,
                    [mapping]: fieldValue,
                  }));
                }
              }}
              onChange={async (mapping, value) => {
                const fieldValue = value as LorFieldValue;
                setLorFieldEdits((prev) => ({
                  ...prev,
                  [mapping]: fieldValue,
                }));
                try {
                  if (mapping === DocumentFieldMapping.POLICY_NUMBER) {
                    await persistPolicyNumber(toDisplayString(fieldValue));
                  } else if (mapping === DocumentFieldMapping.CLAIM_NUMBER) {
                    await persistClaimNumber(toDisplayString(fieldValue));
                  } else if (mapping === DocumentFieldMapping.ADJUSTER_NAME) {
                    await persistAdjuster({
                      adjusterName: toDisplayString(fieldValue),
                    });
                  } else if (
                    mapping === DocumentFieldMapping.ADJUSTER_CONTACT ||
                    mapping === DocumentFieldMapping.CONTACT_METHOD
                  ) {
                    let method: string | undefined;
                    let handleValue: string | undefined;
                    let choice: LoRScalarChoice | null = null;

                    if (mapping === DocumentFieldMapping.ADJUSTER_CONTACT) {
                      if (isScalarChoiceValue(fieldValue)) {
                        choice = fieldValue;
                        handleValue =
                          fieldValue.context?.contact_handle?.value ||
                          fieldValue.value;
                        method =
                          fieldValue.context?.contact_handle?.handle_type;
                      }
                    } else if (typeof fieldValue === 'string') {
                      method = fieldValue.trim().toUpperCase() || undefined;
                    }

                    const override: Partial<typeof finalizeDetails> = {};

                    if (choice) {
                      override.adjusterContactChoice = choice;
                      updateFinalizeDetail('adjusterContactChoice', choice);
                    } else if (
                      mapping === DocumentFieldMapping.ADJUSTER_CONTACT
                    ) {
                      updateFinalizeDetail('adjusterContactChoice', null);
                    }

                    if (handleValue) {
                      if (method === 'FAX') {
                        override.adjusterFax = handleValue;
                        updateFinalizeDetail('adjusterFax', handleValue);
                      } else if (method === 'EMAIL') {
                        override.adjusterEmail = handleValue;
                        updateFinalizeDetail('adjusterEmail', handleValue);
                      } else if (method === 'PHONE') {
                        override.adjusterPhone = handleValue;
                        updateFinalizeDetail('adjusterPhone', handleValue);
                      }
                    }

                    if (
                      mapping === DocumentFieldMapping.ADJUSTER_CONTACT &&
                      method
                    ) {
                      setLorFieldEdits((prev) => ({
                        ...prev,
                        [DocumentFieldMapping.CONTACT_METHOD]: method,
                      }));
                    }

                    if (Object.keys(override).length > 0) {
                      await persistAdjuster(override);
                    }
                  } else if (
                    mapping === DocumentFieldMapping.CARRIER_ADDRESS_1
                  ) {
                    await updateCoverage({
                      matterId,
                      coverageId,
                      coverage: {
                        carrier: {
                          name: coverage?.carrier?.name ?? null,
                          handles: coverage?.carrier?.handles ?? null,
                          address: toDisplayString(fieldValue),
                        },
                      },
                    }).unwrap();
                    saveFinalizeDetails();
                  } else if (mapping === DocumentFieldMapping.CARRIER_NAME) {
                    await updateCoverage({
                      matterId,
                      coverageId,
                      coverage: {
                        carrier: {
                          name: toDisplayString(fieldValue) || null,
                          handles: coverage?.carrier?.handles ?? null,
                          address: coverage?.carrier?.address ?? null,
                        },
                      },
                    }).unwrap();
                    saveFinalizeDetails();
                  }
                  try {
                    await lorDataUpdated({
                      matterId,
                      coverageId,
                      body: {
                        document_id: previewDocId || null,
                        mapping,
                        old_value: undefined,
                        new_value:
                          typeof fieldValue === 'string'
                            ? fieldValue
                            : ((fieldValue as LoRScalarChoice | undefined)
                                ?.value ?? null),
                      },
                    }).unwrap();
                  } catch {
                    // ignore audit failures
                  }
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('Failed to persist LoR required field', err);
                }
              }}
            />
          </div>

          {isTemplateArchived && (
            <ArchivedTemplateWarning
              templateStatus={templateStatus}
              templateName={archivedTemplateName}
              isError={isArchiveCheckError}
              onSelectNewTemplate={() => {
                setSelectedTemplateId(null);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                toast.info(
                  'Please select an active template above and regenerate the LOR.',
                );
              }}
            />
          )}

          <div className="flex justify-end">
            <Button
              size="sm"
              variant="default"
              onClick={() => setIsFaxWizardOpen(true)}
              disabled={isTemplateArchived || isArchiveCheckLoading}
            >
              Send LOR via Fax
            </Button>
          </div>

          <LorList
            matterId={matterId}
            docs={lorDocs}
            isLoading={isDocsLoading}
            docsError={docsError}
            templatesError={templatesError}
            backendHost={backendHost}
            coveragesMap={coveragesMap}
            currentCoverageId={coverageId}
            selectedDocId={previewDocId}
            onView={(id: string) => handleViewLor(id)}
            onLoad={(id: string) => handleLoadLorFromList(id)}
          />
        </div>

        {/* Right Panel: PDF Preview / Chat Toggle */}
        <div className="flex-[5] h-full min-h-[420px] w-full min-w-0 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Panel Header with Toggle */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Button
                  variant={rightPanelMode === 'pdf' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setRightPanelMode('pdf')}
                  className="gap-1.5"
                >
                  <FileText className="h-4 w-4" />
                  PDF Preview
                </Button>
                <Button
                  variant={rightPanelMode === 'chat' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setRightPanelMode('chat')}
                  className="gap-1.5"
                >
                  <MessageSquare className="h-4 w-4" />
                  FinchGPT
                </Button>
              </div>
              {/* Chat-specific controls when in chat mode */}
              {rightPanelMode === 'chat' && (
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedCoverageThreadId ?? undefined}
                    onValueChange={(value) =>
                      setSelectedCoverageThreadId(value)
                    }
                    disabled={
                      isCoverageThreadLoading ||
                      coverageThreadOptions.length === 0
                    }
                  >
                    <SelectTrigger
                      className="h-8 w-48 text-xs"
                      aria-label="Select coverage FinchGPT thread"
                    >
                      <SelectValue
                        placeholder={
                          isCoverageThreadLoading
                            ? 'Loading threads…'
                            : 'Select thread'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {coverageThreadOptions.map((thread) => (
                        <SelectItem key={thread.id} value={thread.id}>
                          {thread.title ||
                            `Coverage Review ${thread.id.slice(0, 8)}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleOpenRenameThreadDialog}
                    disabled={
                      !selectedCoverageThreadId ||
                      isCoverageThreadLoading ||
                      updateCoverageThreadState.isLoading
                    }
                    aria-label="Rename FinchGPT thread"
                    title="Rename thread"
                  >
                    <Pencil className="h-4 w-4 text-zinc-500" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateNewCoverageThread}
                    disabled={
                      createCoverageThreadState.isLoading ||
                      !matterId ||
                      !coverageId
                    }
                  >
                    {createCoverageThreadState.isLoading ? 'Creating…' : 'New'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleOpenCoverageAssistant}
                    aria-label="Pop out FinchGPT assistant"
                    title="Open in drawer"
                  >
                    <Maximize2 className="h-4 w-4 text-zinc-500" />
                  </Button>
                </div>
              )}
              {/* PDF-specific controls when in PDF mode */}
              {rightPanelMode === 'pdf' && (
                <div className="flex items-center gap-2">
                  {currentDocInstance && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditDocument}
                      disabled={isEditorLoading || !currentDocInstance}
                      className="gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {isEditorLoading ? 'Loading…' : 'Edit'}
                    </Button>
                  )}
                  {isPdfFromCache && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                      Cached
                    </span>
                  )}
                  {cachedPreviewUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refreshCachedPreview}
                      disabled={isCachedPreviewLoading}
                    >
                      Refresh
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Panel Content */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {rightPanelMode === 'pdf' ? (
                // PDF Preview Mode
                <div className="h-full flex flex-col">
                  {isCachedPreviewLoading || isRegeneratingPreview ? (
                    <div className="flex-1 flex items-center justify-center bg-zinc-50">
                      <div className="text-center space-y-3">
                        <div className="animate-spin h-8 w-8 border-2 border-zinc-300 border-t-zinc-600 rounded-full mx-auto" />
                        <p className="text-sm text-zinc-500">
                          {isRegeneratingPreview
                            ? 'Generating updated preview…'
                            : 'Loading PDF preview…'}
                        </p>
                      </div>
                    </div>
                  ) : cachedPreviewUrl ? (
                    <iframe
                      src={`${cachedPreviewUrl}#navpanes=0`}
                      className="w-full h-full border-0"
                      title="LOR PDF Preview"
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center bg-zinc-50">
                      <div className="text-center space-y-2 px-4">
                        <FileText className="h-12 w-12 text-zinc-300 mx-auto" />
                        <p className="text-sm text-zinc-500">
                          No LOR document generated yet
                        </p>
                        <p className="text-xs text-zinc-400">
                          Use &quot;Check LOR&quot; to generate a Letter of
                          Representation
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Chat Mode
                <div className="h-full">
                  {isCoverageThreadLoading || !selectedCoverageThreadId ? (
                    <div className="p-4 space-y-3">
                      <div className="h-4 rounded bg-zinc-100 animate-pulse" />
                      <div className="h-4 rounded bg-zinc-100 animate-pulse delay-75" />
                      <div className="h-4 rounded bg-zinc-100 animate-pulse delay-150" />
                      <p className="text-xs text-zinc-500 mt-2">
                        Preparing FinchGPT coverage context…
                      </p>
                    </div>
                  ) : (
                    <EditorProvider>
                      <div className="h-full">
                        <AgentChatThread
                          matterId={matterId}
                          threadId={selectedCoverageThreadId}
                          workspaceId={coverageId}
                          coverageId={coverageId}
                        />
                      </div>
                    </EditorProvider>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={isFaxWizardOpen} onOpenChange={setIsFaxWizardOpen}>
        <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">Send Fax</DialogTitle>
          <div className="flex-1 overflow-auto">
            <FaxWizard
              matterId={matterId}
              initialData={{
                recipient_number:
                  finalizeDetails.adjusterFax ||
                  fallbackFax ||
                  fallbackCarrierFax ||
                  '',
                attachment_blob_ids: [],
                attachment_document_ids: lorDocsForCoverage.map((d) => d.id),
              }}
              documentItems={lorDocsForCoverage.map((d) => ({
                id: d.id,
                title: d.document_template?.name || 'Letter of Representation',
                createdAt: d.created_at,
              }))}
              onSuccess={async () => {
                setIsFaxWizardOpen(false);
                toast.success('Fax flow completed');
                await markSendLorTaskCompleted();
              }}
              onCancel={() => setIsFaxWizardOpen(false)}
            />
          </div>
        </RightDialogContent>
      </Dialog>
      <Dialog
        open={isRenameThreadDialogOpen}
        onOpenChange={handleCloseRenameThreadDialog}
      >
        <DialogContent className="max-w-md">
          <DialogTitle>Rename coverage FinchGPT thread</DialogTitle>
          <DialogDescription>
            Give this conversation a descriptive name so teammates know what the
            thread covers.
          </DialogDescription>
          <div className="space-y-2">
            <Input
              value={renameThreadName}
              onChange={(event) => setRenameThreadName(event.target.value)}
              placeholder={selectedThread?.title ?? 'Coverage FinchGPT Review'}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => handleCloseRenameThreadDialog(false)}
              disabled={updateCoverageThreadState.isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameThread}
              disabled={
                updateCoverageThreadState.isLoading ||
                renameThreadName.trim().length === 0
              }
            >
              {updateCoverageThreadState.isLoading ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <LorPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        isLoading={isPreviewLoading}
        previewUrl={previewUrlHook}
        onDownloadPdf={() => {
          if (previewUrlHook) {
            const a = document.createElement('a');
            a.href = previewUrlHook;
            a.download = previewFilename;
            a.click();
          }
        }}
        onDownloadDocx={() => {
          if (previewDocId) {
            const url = `${backendHost}/api/matters/${matterId}/documents/${previewDocId}/download/`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      />
      <AgentChatDrawer
        matterId={matterId}
        isOpen={isCoverageAssistantOpen}
        onClose={handleCloseCoverageAssistant}
      />
      <UploadLorReviewModeModal
        open={isUploadModeModalOpen}
        onOpenChange={setIsUploadModeModalOpen}
        templates={displayTemplates}
        isLoadingTemplates={isTemplatesLoading}
        onConfirm={handleUploadModeConfirm}
        isUploading={isProcessingCustomLor}
      />
    </div>
  );
};
